import { getAdminEndpoint } from '../endpoints/adminEndpointManager';

// Helper function to decode JWT and check expiration
const isJWTExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    return payload.exp ? payload.exp < now : false;
  } catch (e) {
    console.warn('Failed to decode JWT:', e);
    return true; // Treat invalid tokens as expired
  }
};

// ========== NEW ADMIN AUTHENTICATION FUNCTIONS ==========

// Storage keys for new admin auth
const ADMIN_TOKEN_KEY = 'adminAuthToken';
const ADMIN_USER_KEY = 'adminUser';
const ADMIN_SESSION_KEY = 'adminSession';

// Set admin token
export const setAdminToken = (token) => {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  console.log('Admin token stored');
};

// Get admin token
export const getAdminToken = () => {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
};

// Set admin user data
export const setAdminUser = (userData) => {
  localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(userData));
  console.log('Admin user data stored');
};

// Get admin user data
export const getAdminUser = () => {
  try {
    const userData = localStorage.getItem(ADMIN_USER_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch (e) {
    console.error('Failed to parse admin user data:', e);
    return null;
  }
};

// Set admin session data
export const setAdminSession = (sessionData) => {
  localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(sessionData));
  console.log('Admin session stored');
};

// Get admin session data
export const getAdminSession = () => {
  try {
    const session = localStorage.getItem(ADMIN_SESSION_KEY);
    return session ? JSON.parse(session) : null;
  } catch (e) {
    console.error('Failed to parse admin session:', e);
    return null;
  }
};

// Clear all admin auth data
export const clearAdminAuthData = () => {
  const keysToRemove = [
    ADMIN_TOKEN_KEY,
    ADMIN_USER_KEY,
    ADMIN_SESSION_KEY,
    'admin-token',
    'admin-session',
    'jwt',
    'adminToken'
  ];
  
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
  
  console.log('Admin auth data cleared');
  
  // Dispatch logout event
  window.dispatchEvent(new Event('admin-auth-logout'));
};

// Check if admin is authenticated (new auth system)
export const isAdminAuth = () => {
  const token = getAdminToken();
  const session = getAdminSession();
  
  if (!token) return false;
  
  // Check session expiration
  if (session?.expiresAt && new Date() > new Date(session.expiresAt)) {
    return false;
  }
  
  // Check JWT expiration
  if (isJWTExpired(token)) {
    return false;
  }
  
  return true;
};

// Get current admin user
export const getCurrentAdmin = () => {
  return getAdminUser();
};

// Check if admin has permission
export const adminHasPermission = (permission) => {
  const admin = getAdminUser();
  if (!admin) return false;

  // Super admin has all permissions (check both isSuperAdmin flag and role)
  if (admin.isSuperAdmin || admin.role === 'super_admin') return true;

  // Check flat array permissions (backward compatibility)
  if (Array.isArray(admin.permissions) && admin.permissions.includes(permission)) return true;

  // Check nested permission structure (sub-role system)
  // Map permission strings like 'users.read' to nested structure { users: { canView: true } }
  if (typeof permission === 'string' && permission.includes('.')) {
    const [category, action] = permission.split('.');
    
    // Map action names to permission keys in nested structure
    const actionMap = {
      'read': 'canView',
      'write': 'canCreate',
      'edit': 'canEdit',
      'delete': 'canDelete',
      'manage': 'canManage',
      'block': 'canBlockUnblock',
      'viewHistory': 'canViewHistory',
      'manageRoles': 'canManageRoles',
      'selectPrimary': 'canSelectPrimary'
    };
    
    const permissionKey = actionMap[action] || action;
    
    // Check if category exists and has the permission
    return admin.permissions?.[category]?.[permissionKey] === true;
  }

  return false;
};

// Check if admin has role
export const adminHasRole = (role) => {
  const admin = getAdminUser();
  if (!admin) return false;
  return admin.role === role;
};

// Check role hierarchy - returns true if admin's role is >= target role level
export const hasRoleLevel = (targetRole) => {
  const admin = getAdminUser();
  if (!admin) return false;
  
  const roleLevels = {
    'viewer': 1,
    'moderator': 2,
    'admin': 3,
    'super_admin': 4
  };
  
  const adminLevel = roleLevels[admin.role] || 0;
  const targetLevel = roleLevels[targetRole] || 0;
  
  return adminLevel >= targetLevel;
};

// ========== END NEW ADMIN AUTHENTICATION FUNCTIONS ==========

// Utility function to get valid admin token with expiration check
export const getValidAdminToken = () => {
  // Check new token key first
  const newToken = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (newToken) {
    const session = getAdminSession();
    // Check session expiration
    if (session?.expiresAt && new Date() > new Date(session.expiresAt)) {
      console.log('Token expired based on session info, logging out');
      adminLogout();
      return null;
    }
    
    // Also check JWT expiration as backup
    if (isJWTExpired(newToken)) {
      console.log('JWT token is expired, logging out');
      adminLogout();
      return null;
    }
    
    return newToken;
  }
  
  // Fall back to old token key for backwards compatibility
  const token = localStorage.getItem('admin-token');
  const sessionInfo = JSON.parse(localStorage.getItem('admin-session') || '{}');
  
  if (!token) return null;
  
  // Check session expiration first
  if (sessionInfo.expiresAt && new Date() > new Date(sessionInfo.expiresAt)) {
    console.log('Token expired based on session info, logging out');
    adminLogout();
    return null;
  }
  
  // Also check JWT expiration as backup
  if (isJWTExpired(token)) {
    console.log('JWT token is expired, logging out');
    adminLogout();
    return null;
  }
  
  return token;
};

// Admin logout function
export const adminLogout = () => {
  console.log('Admin logout initiated');
  
  // Clean up ALL localStorage keys used by the application
  const keysToRemove = [
    'admin-token',
    'admin-session',
    'available-endpoints',
    'selected-endpoint',
    'primary-endpoint-confirmed',
    'selected-environment',
    'jwt',
    'adminToken'
  ];
  
  // Remove each key and log for debugging
  keysToRemove.forEach(key => {
    const existed = localStorage.getItem(key) !== null;
    localStorage.removeItem(key);
    if (existed) {
      console.log(`Cleaned up localStorage key: ${key}`);
    }
  });
  
  // Also clean up any other potential admin-related keys with a more aggressive approach
  // This handles any future keys that might be added
  const allKeys = Object.keys(localStorage);
  const adminKeys = allKeys.filter(key =>
    key.includes('admin') ||
    key.includes('token') ||
    key.includes('session') ||
    key.includes('endpoint') ||
    key.includes('auth')
  );
  
  adminKeys.forEach(key => {
    if (!keysToRemove.includes(key)) {
      localStorage.removeItem(key);
      console.log(`Cleaned up additional localStorage key: ${key}`);
    }
  });
  
  // Reset CSS custom properties to default values
  const root = document.documentElement;
  root.style.setProperty('--env-primary-color', '#4361EE');
  root.style.setProperty('--env-background-color', '#f8f9fa');
  
  // Reset page title
  document.title = 'Admin Dashboard';
  
  // Dispatch multiple events to ensure all components are notified
  window.dispatchEvent(new Event('admin-logout'));
  window.dispatchEvent(new Event('logout'));
  window.dispatchEvent(new Event('token-expired'));
  
  // Dispatch environment reset event to notify components
  window.dispatchEvent(new CustomEvent('environment-reset', {
    detail: {
      message: 'Environment reset to default after logout',
      defaultEnvironment: 'web2_0'
    }
  }));
};

// Comprehensive cleanup function for all localStorage data
export const clearAllLocalStorage = () => {
  console.log('Clearing all localStorage data');
  localStorage.clear();
  console.log('All localStorage data cleared');
};

// Clean up only admin-related localStorage data
export const clearAdminLocalStorage = () => {
  console.log('Clearing admin-related localStorage data');
  
  const keysToRemove = [
    'admin-token',
    'admin-session',
    'available-endpoints',
    'selected-endpoint',
    'primary-endpoint-confirmed',
    'selected-environment',
    'jwt',
    'adminToken'
  ];
  
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
  
  console.log('Admin localStorage data cleared');
};

// Debug function to list all localStorage keys and their values
export const debugLocalStorage = () => {
  console.log('=== localStorage Debug ===');
  const allKeys = Object.keys(localStorage);
  
  if (allKeys.length === 0) {
    console.log('localStorage is empty');
    return;
  }
  
  console.log(`Found ${allKeys.length} localStorage keys:`);
  allKeys.forEach(key => {
    const value = localStorage.getItem(key);
    let displayValue = value;
    
    // Try to parse JSON values for better display
    try {
      const parsed = JSON.parse(value);
      displayValue = typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : parsed;
    } catch (e) {
      // Not JSON, use as-is
    }
    
    // Truncate long values
    if (typeof displayValue === 'string' && displayValue.length > 100) {
      displayValue = displayValue.substring(0, 100) + '...';
    }
    
    console.log(`  ${key}:`, displayValue);
  });
  console.log('=== End localStorage Debug ===');
  
  return allKeys;
};

// Function to check for any remaining admin-related localStorage data
export const checkRemainingAdminData = () => {
  const allKeys = Object.keys(localStorage);
  const adminKeys = allKeys.filter(key => 
    key.includes('admin') || 
    key.includes('token') || 
    key.includes('session') || 
    key.includes('endpoint') ||
    key.includes('auth')
  );
  
  if (adminKeys.length > 0) {
    console.warn('Found remaining admin-related localStorage keys:', adminKeys);
    return adminKeys;
  } else {
    console.log('No remaining admin-related localStorage data found');
    return [];
  }
};

// Handle IP validation errors - redirects to login and clears cache
export const handleIpError = (error, response) => {
  const currentSession = JSON.parse(localStorage.getItem('admin-session') || '{}');
  const currentIp = error.currentIp || response?.data?.securityInfo?.currentIp;
  const sessionId = error.sessionId || response?.data?.securityInfo?.sessionId;
  
  console.error('[authManager] IP validation error:', {
    currentIp,
    sessionId,
    errorMessage: error.error || error.message || 'IP not authorized',
    hint: error.hint
  });
  
  // Dispatch custom event for IP error handling (for components that may want to show UI)
  window.dispatchEvent(new CustomEvent('ip-validation-error', {
    detail: {
      currentIp,
      authorizedIps: currentSession.authorizedIps || [],
      sessionId,
      errorMessage: error.error || error.message || 'IP not authorized',
      hint: error.hint
    }
  }));
  
  // Clear all admin auth data immediately
  clearAdminAuthData();
  
  // Also call adminLogout for comprehensive cleanup
  adminLogout();
  
  // Redirect to login page after a short delay to allow cleanup
  setTimeout(() => {
    window.location.href = '/login?reason=ip_mismatch';
  }, 100);
};

// Check if error is an IP address error
export const isIpAddressError = (error) => {
  if (!error?.response?.data) return false;
  const errorData = error.response.data;
  return errorData?.error === 'Access denied - IP address not authorized' ||
         errorData?.code === 'IP_NOT_AUTHORIZED' ||
         errorData?.details?.includes('IP address');
};

// Debug function to check current authentication state
export const debugAuthState = () => {
  const token = localStorage.getItem('admin-token');
  const session = localStorage.getItem('admin-session');
  const endpoints = localStorage.getItem('available-endpoints');
  const selectedEndpoint = localStorage.getItem('selected-endpoint');
  
  console.log('=== Authentication State Debug ===');
  console.log('Token:', token ? `${token.substring(0, 30)}...` : 'missing');
  console.log('Session:', session ? JSON.parse(session) : 'missing');
  console.log('Available endpoints:', endpoints ? JSON.parse(endpoints) : 'missing');
  console.log('Selected endpoint:', selectedEndpoint);
  console.log('API Config:', {
    ADMIN_ENDPOINT: getAdminEndpoint()
  });
  console.log('=== End Debug ===');
};

// Enhanced auto-logout check every 30 seconds (more frequent)
let autoLogoutInterval;

const startAutoLogoutCheck = () => {
  if (autoLogoutInterval) {
    clearInterval(autoLogoutInterval);
  }
  
  autoLogoutInterval = setInterval(() => {
    const rawToken = localStorage.getItem('admin-token');
    if (rawToken) {
      const validToken = getValidAdminToken();
      if (!validToken) {
        console.log('Auto-logout check: Token expired, logging out...');
        // adminLogout() is already called by getValidAdminToken() if token is expired
      }
    }
  }, 30000); // Check every 30 seconds
};

// Start the auto-logout check
startAutoLogoutCheck();

// Debug function to manually test authentication
export const debugAuthentication = () => {
  console.log('=== DEBUG AUTHENTICATION ===');
  
  // 1. Check localStorage
  const token = localStorage.getItem('admin-token');
  const session = localStorage.getItem('admin-session');
  
  console.log('1. localStorage check:');
  console.log('  Token exists:', !!token);
  console.log('  Token preview:', token ? `${token.substring(0, 50)}...` : 'MISSING');
  console.log('  Session exists:', !!session);
  
  if (session) {
    try {
      const sessionData = JSON.parse(session);
      console.log('  Session data:', sessionData);
      console.log('  Session expires:', sessionData.expiresAt);
      console.log('  Current time:', new Date().toISOString());
      
      if (sessionData.expiresAt) {
        const isExpired = new Date() > new Date(sessionData.expiresAt);
        console.log('  Is expired:', isExpired);
      }
    } catch (e) {
      console.log('  Session parse error:', e.message);
    }
  }
  
  // 2. Check getValidAdminToken function
  console.log('2. getValidAdminToken() result:');
  const validToken = getValidAdminToken();
  console.log('  Valid token:', !!validToken);
  console.log('  Valid token preview:', validToken ? `${validToken.substring(0, 50)}...` : 'NONE');
  
  // 3. Check JWT expiration if token exists
  if (token) {
    console.log('3. JWT expiration check:');
    const jwtExpired = isJWTExpired(token);
    console.log('  JWT is expired:', jwtExpired);
  }
  
  console.log('=== END DEBUG ===');
  
  return {
    hasToken: !!token,
    hasValidToken: !!validToken,
    tokenPreview: token ? `${token.substring(0, 20)}...` : null,
    jwtExpired: token ? isJWTExpired(token) : null
  };
};

export const checkTokenValidity = () => {
  const token = getValidAdminToken();
  const session = JSON.parse(localStorage.getItem('admin-session') || '{}');
  
  console.log('=== Token Validity Check ===');
  console.log('Token exists:', !!token);
  console.log('Token preview:', token ? `${token.substring(0, 50)}...` : 'none');
  console.log('Session expires at:', session.expiresAt);
  console.log('Current time:', new Date().toISOString());
  console.log('Token is valid:', !!token);
  
  if (session.expiresAt) {
    const isExpired = new Date() > new Date(session.expiresAt);
    console.log('Token is expired:', isExpired);
  }
  
  return !!token;
}; 